// Merge Phoenix native agend do legacy sekcí hlavního menu.
// Fáze 1: native položky se vkládají na ZAČÁTEK odpovídající sekce (dle labelu); položka s `group`
// se vloží do stejnojmenné vnořené skupiny (vytvoří se, pokud chybí).
// Sekce, která v legacy (zatím) není, se vytvoří jako native-only (agendy nezmizí).

import type { MenuGroupNode, MenuItemNode, MenuNode, MenuSection } from '@/shared/ui';

/** Deklarace native agendy do menu. */
export interface NativeMenuEntry {
  /** Navigační path — `onOpen` (native) na ni naviguje. */
  readonly id: string;
  readonly title: string;
  /**
   * Matching klíč cílové root sekce proti legacy labelu a zároveň zobrazený popisek
   * native-only sekce. Lokalizovaný (musí se shodovat s Magic labelem, např. 'Produkty'/'Products').
   */
  readonly section: string;
  /**
   * Cílová vnořená skupina uvnitř sekce (matching klíč proti legacy skupině, např. 'Číselníky').
   * Bez `group` jde položka na úroveň sekce.
   */
  readonly group?: string;
  /** GlobalName pro type-ikonu (volitelně). */
  readonly key?: string;
}

function toNode(entry: NativeMenuEntry): MenuItemNode {
  return { t: 'item', id: entry.id, title: entry.title, key: entry.key, origin: 'native' };
}

/** Vloží native položky do položek sekce: ne-skupinové na začátek, skupinové do (příp. nové) skupiny. */
function mergeIntoSectionItems(
  sectionItems: readonly MenuNode[],
  entries: NativeMenuEntry[]
): MenuNode[] {
  const ungrouped = entries.filter((entry) => !entry.group).map(toNode);

  let items: MenuNode[] = [...sectionItems];
  const byGroup = new Map<string, NativeMenuEntry[]>();
  for (const entry of entries) {
    if (!entry.group) continue;
    const arr = byGroup.get(entry.group);
    if (arr) arr.push(entry);
    else byGroup.set(entry.group, [entry]);
  }

  for (const [groupTitle, groupEntries] of byGroup) {
    const nodes = groupEntries.map(toNode);
    const index = items.findIndex((node) => node.t === 'group' && node.title === groupTitle);
    if (index >= 0) {
      const existing = items[index] as MenuGroupNode;
      items[index] = { ...existing, items: [...nodes, ...existing.items] };
    } else {
      const group: MenuGroupNode = {
        t: 'group',
        id: `native-group-${groupTitle}`,
        title: groupTitle,
        items: nodes,
      };
      items = [group, ...items];
    }
  }

  return [...ungrouped, ...items];
}

export function mergeNativeIntoSections(
  sections: readonly MenuSection[],
  native: readonly NativeMenuEntry[]
): MenuSection[] {
  if (native.length === 0) return sections.map((s) => ({ ...s }));

  // Native položky seskupené dle cílové sekce (zachovává pořadí deklarace).
  const byLabel = new Map<string, NativeMenuEntry[]>();
  for (const entry of native) {
    const arr = byLabel.get(entry.section);
    if (arr) arr.push(entry);
    else byLabel.set(entry.section, [entry]);
  }

  const usedLabels = new Set<string>();
  const merged: MenuSection[] = sections.map((s) => {
    const entries = byLabel.get(s.label);
    if (!entries) return s;
    usedLabels.add(s.label);
    return { ...s, items: mergeIntoSectionItems(s.items, entries) };
  });

  // Sekce bez legacy protějšku → native-only (na konec, v pořadí prvního výskytu).
  for (const [label, entries] of byLabel) {
    if (usedLabels.has(label)) continue;
    merged.push({ id: `native-${label}`, label, items: mergeIntoSectionItems([], entries) });
  }

  return merged;
}
